import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { OG_LOCALE_MAP, type SupportedLocale } from '../../config/localeConfig';
import { buildHreflangLinks, type LocalePublishState } from '../../seo/buildHreflangLinks';

function normalizeSiteUrl(value?: string) {
  const fallback = 'https://www.pearlvoyages.com';
  const raw = String(value || fallback).replace(/\/+$/, '');
  try {
    const parsed = new URL(raw);
    if (parsed.hostname === 'pearlvoyages.com') parsed.hostname = 'www.pearlvoyages.com';
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return fallback;
  }
}

const SITE_URL = normalizeSiteUrl(import.meta.env.VITE_PUBLIC_SITE_URL);

export function HreflangTags({
  locale,
  pathname,
  localePublishState,
}: {
  locale: SupportedLocale;
  pathname: string;
  localePublishState?: LocalePublishState;
}) {
  const links = useMemo(
    () =>
      buildHreflangLinks({
        siteUrl: SITE_URL,
        pathname,
        localePublishState,
      }),
    [pathname, localePublishState]
  );

  const ogLocale = OG_LOCALE_MAP[locale] || OG_LOCALE_MAP.en;
  const ogAlternates = links
    .filter((item) => item.hreflang !== locale && item.hreflang !== 'x-default')
    .map((item) => OG_LOCALE_MAP[item.hreflang as SupportedLocale])
    .filter(Boolean);

  const canonical = links.find((item) => item.hreflang === locale)?.href;

  return (
    <Helmet>
      {canonical ? <link rel="canonical" href={canonical} /> : null}

      {links.map((item) => (
        <link
          key={item.hreflang}
          rel="alternate"
          hrefLang={item.hreflang}
          href={item.href}
        />
      ))}

      <meta property="og:locale" content={ogLocale} />
      {ogAlternates.map((value) => (
        <meta key={value} property="og:locale:alternate" content={value} />
      ))}
    </Helmet>
  );
}
